import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Button from "@amzn/awsui-components-react/polaris/button";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import DatePicker from "@amzn/awsui-components-react/polaris/date-picker";

export interface SiglineFormState {
  text: string;
  quantityPerDose: string;
  quantityPerDay: string;
  hoaCode: string;
  hoaTimes: string[];
  units: string;
  scheduleType: "everyday" | "dow" | "period" | "";
  period: string;
  dow: boolean[];
  startDatetime: string;
  endDatetime: string;
  newHoaTime: string;
  rawSigCode: string;
  lineNumber: string;
  version: string;
  variableDosage: boolean;
  variableFrequency: boolean;
  minDosage: string;
  maxDosage: string;
  multiDosage: boolean;
  frequency: string;
  minFrequency: string;
  maxFrequency: string;
  alternatingPrescription: boolean;
  prescriberDirectedHoa: string;
  timesOfDay: string;
  withMeals: string;
}

export const EMPTY_SIGLINE: SiglineFormState = {
  text: "",
  quantityPerDose: "",
  quantityPerDay: "",
  hoaCode: "",
  hoaTimes: [],
  units: "",
  scheduleType: "",
  period: "",
  dow: [false, false, false, false, false, false, false],
  startDatetime: "",
  endDatetime: "",
  newHoaTime: "",
  rawSigCode: "",
  lineNumber: "",
  version: "",
  variableDosage: false,
  variableFrequency: false,
  minDosage: "",
  maxDosage: "",
  multiDosage: false,
  frequency: "",
  minFrequency: "",
  maxFrequency: "",
  alternatingPrescription: false,
  prescriberDirectedHoa: "",
  timesOfDay: "",
  withMeals: "",
};

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface Props {
  sigline: SiglineFormState;
  index: number;
  onChange: (updates: Partial<SiglineFormState>) => void;
  onRemove: () => void;
}

class SiglineBlock extends React.Component<Props> {
  private addHoaTime = () => {
    const { sigline, onChange } = this.props;
    if (!sigline.newHoaTime) return;
    onChange({ hoaTimes: [...sigline.hoaTimes, sigline.newHoaTime], newHoaTime: "" });
  };

  private removeHoaTime = (timeIndex: number) => {
    const { sigline, onChange } = this.props;
    onChange({ hoaTimes: sigline.hoaTimes.filter((_, i) => i !== timeIndex) });
  };

  private toggleDow = (dayIndex: number) => {
    const { sigline, onChange } = this.props;
    const dow = [...sigline.dow];
    dow[dayIndex] = !dow[dayIndex];
    onChange({ dow });
  };

  render() {
    const { sigline: sig, index, onChange, onRemove } = this.props;

    return (
      <Container
        header={
          <Header variant="h3" actions={
            <Button iconName="remove" variant="icon" onClick={onRemove} />
          }>
            SIG Line {index + 1}
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Start Date">
              <DatePicker
                value={sig.startDatetime}
                onChange={({ detail }) => onChange({ startDatetime: detail.value })}
                placeholder="YYYY/MM/DD"
              />
            </FormField>
            <FormField label="End Date">
              <DatePicker
                value={sig.endDatetime}
                onChange={({ detail }) => onChange({ endDatetime: detail.value })}
                placeholder="YYYY/MM/DD"
              />
            </FormField>
          </ColumnLayout>

          <FormField label="Schedule Type">
            <SpaceBetween size="xs" direction="horizontal">
              {(["everyday", "dow", "period"] as const).map((type) => (
                <Button
                  key={type}
                  variant={sig.scheduleType === type ? "primary" : "normal"}
                  onClick={() => {
                    if (sig.scheduleType === type) {
                      onChange({ scheduleType: "", dow: [false, false, false, false, false, false, false], period: "" });
                    } else {
                      onChange({ scheduleType: type, dow: [false, false, false, false, false, false, false], period: "" });
                    }
                  }}
                >
                  {type === "everyday" ? "Every Day" : type === "dow" ? "Days of Week" : "Period"}
                </Button>
              ))}
            </SpaceBetween>
          </FormField>

          {sig.scheduleType === "dow" && (
            <FormField label="Days of Week">
              <SpaceBetween size="xs" direction="horizontal">
                {DOW_LABELS.map((day, dayIdx) => (
                  <Button
                    key={day}
                    variant={sig.dow[dayIdx] ? "primary" : "normal"}
                    onClick={() => this.toggleDow(dayIdx)}
                  >
                    {day}
                  </Button>
                ))}
              </SpaceBetween>
            </FormField>
          )}

          {sig.scheduleType === "period" && (
            <FormField label="Period (every N days)">
              <Input
                type="number"
                value={sig.period}
                onChange={({ detail }) => onChange({ period: detail.value })}
              />
            </FormField>
          )}

          <ColumnLayout columns={3}>
            <FormField label="Qty Per Dose">
              <Input value={sig.quantityPerDose} onChange={({ detail }) => onChange({ quantityPerDose: detail.value })} />
            </FormField>
            <FormField label="Units">
              <Input value={sig.units} onChange={({ detail }) => onChange({ units: detail.value })} placeholder="e.g. TAB" />
            </FormField>
            <FormField label="Qty Per Day">
              <Input value={sig.quantityPerDay} onChange={({ detail }) => onChange({ quantityPerDay: detail.value })} />
            </FormField>
          </ColumnLayout>

          <FormField label="HOA Code">
            <Input value={sig.hoaCode} onChange={({ detail }) => onChange({ hoaCode: detail.value })} placeholder='e.g. 01  ~1~' />
          </FormField>

          <FormField label="HOA Times">
            <SpaceBetween size="xs" direction="horizontal">
              {sig.hoaTimes.map((time, tIdx) => (
                <Button key={tIdx} variant="normal" onClick={() => this.removeHoaTime(tIdx)}>
                  {time} ✕
                </Button>
              ))}
              <Input
                value={sig.newHoaTime}
                onChange={({ detail }) => onChange({ newHoaTime: detail.value })}
                placeholder="e.g. 0800"
              />
              <Button iconName="add-plus" variant="icon" onClick={this.addHoaTime} />
            </SpaceBetween>
          </FormField>

          <FormField label="English Directions">
            <Textarea
              value={sig.text}
              onChange={({ detail }) => onChange({ text: detail.value })}
              placeholder="Take 1 tablet by mouth daily."
              rows={2}
            />
          </FormField>

          <ExpandableSection headerText="Advanced Fields">
            <SpaceBetween size="m">
              <ColumnLayout columns={3}>
                <FormField label="Raw SIG Code">
                  <Input value={sig.rawSigCode} onChange={({ detail }) => onChange({ rawSigCode: detail.value })} />
                </FormField>
                <FormField label="Line Number">
                  <Input type="number" value={sig.lineNumber} onChange={({ detail }) => onChange({ lineNumber: detail.value })} />
                </FormField>
                <FormField label="Version">
                  <Input type="number" value={sig.version} onChange={({ detail }) => onChange({ version: detail.value })} />
                </FormField>
                <FormField label="Min Dosage">
                  <Input type="number" value={sig.minDosage} onChange={({ detail }) => onChange({ minDosage: detail.value })} />
                </FormField>
                <FormField label="Max Dosage">
                  <Input type="number" value={sig.maxDosage} onChange={({ detail }) => onChange({ maxDosage: detail.value })} />
                </FormField>
                <FormField label="Frequency">
                  <Input type="number" value={sig.frequency} onChange={({ detail }) => onChange({ frequency: detail.value })} />
                </FormField>
                <FormField label="Min Frequency">
                  <Input type="number" value={sig.minFrequency} onChange={({ detail }) => onChange({ minFrequency: detail.value })} />
                </FormField>
                <FormField label="Max Frequency">
                  <Input type="number" value={sig.maxFrequency} onChange={({ detail }) => onChange({ maxFrequency: detail.value })} />
                </FormField>
              </ColumnLayout>

              <SpaceBetween size="s" direction="horizontal">
                <Toggle checked={sig.variableDosage} onChange={({ detail }) => onChange({ variableDosage: detail.checked })}>Variable Dosage</Toggle>
                <Toggle checked={sig.variableFrequency} onChange={({ detail }) => onChange({ variableFrequency: detail.checked })}>Variable Frequency</Toggle>
                <Toggle checked={sig.multiDosage} onChange={({ detail }) => onChange({ multiDosage: detail.checked })}>Multi Dosage</Toggle>
                <Toggle checked={sig.alternatingPrescription} onChange={({ detail }) => onChange({ alternatingPrescription: detail.checked })}>Alternating Prescription</Toggle>
              </SpaceBetween>

              <ColumnLayout columns={3}>
                <FormField label="Prescriber Directed HOA" description="Comma-separated">
                  <Input value={sig.prescriberDirectedHoa} onChange={({ detail }) => onChange({ prescriberDirectedHoa: detail.value })} placeholder="e.g. 0800,1400" />
                </FormField>
                <FormField label="Times of Day" description="Comma-separated">
                  <Input value={sig.timesOfDay} onChange={({ detail }) => onChange({ timesOfDay: detail.value })} placeholder="e.g. Morning,Evening" />
                </FormField>
                <FormField label="With Meals" description="Comma-separated">
                  <Input value={sig.withMeals} onChange={({ detail }) => onChange({ withMeals: detail.value })} placeholder="e.g. Breakfast,Dinner" />
                </FormField>
              </ColumnLayout>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>
    );
  }
}

export default SiglineBlock;
